import { generateName } from '@/lib/name-generator.ts';
import { Me, MockParticipant, Participant } from '@/model/Participant.ts';
import { create } from '@/store/create-disposable-store.ts';
import { Command, Conference, Language, ParticipantJoinRequest, VoiceAssistantState } from 'intermind';
import _ from 'lodash';
import { Session } from 'mind-sdk-web/lib/Session';
import { subscribeWithSelector } from 'zustand/middleware';

export type AVFreezeState = {
  audioFrozen: boolean;
  videoFrozen: boolean;
};

export type AVFreezeMap = Record<string, AVFreezeState>;

export interface ConferenceStore {
  conference: Conference | null;
  setConference: (conference: Conference | null) => void;

  session: Session | null;
  setSession: (session: Session | null) => void;

  raisedCommands: {
    type: Command;
    participantId: string;
  }[];
  setRaisedCommand: (command: { type: Command; participantId: string }) => void;
  deleteRaisedCommand: (command: Command) => void;

  participants: Participant[];
  addParticipant: (participant: Participant) => void;
  addParticipants: (participants: Participant[]) => void;
  updateParticipant: (participant: Participant) => void;
  removeParticipant: (participantId: string) => void;
  subscribeToParticipants: (
    callback: (oldParticipants: Participant[], newParticipants: Participant[]) => void
  ) => () => void;
  me: Me | null;
  setMe: (p: Me | null) => void;

  loudestParticipantId: string | null;
  setLoudestParticipantId: (id: string | null) => void;

  addMockParticipant: () => void;
  removeMockParticipant: () => void;

  joinRequests: ParticipantJoinRequest[];
  addJoinRequest: (request: ParticipantJoinRequest) => void;
  removeJoinRequest: (requestId: string) => void;

  voiceAssistantState: VoiceAssistantState | null;
  setVoiceAssistantState: (state: VoiceAssistantState | null) => void;

  voiceAssistantSpeaking: boolean;
  setVoiceAssistantSpeaking: (speaking: boolean) => void;

  translationEnabled: boolean;
  setTranslationEnabled: (enabled: boolean) => void;

  avScores: AVFreezeMap;
  setParticipantAVScores: (id: string, score: AVFreezeState) => void;

  currentTranslatingState: Record<string, { participantId: string; utteranceId: string }[]>;
  setCurrentTranslatingState: (state: Record<string, { participantId: string; utteranceId: string }[]>) => void;
  myLanguage: string | null;
  setMyLanguage: (lang: string) => void;

  participantVolumes: Record<string, number>;
  setParticipantVolumes: (ids: Record<string, number>) => void;

  commandTimers: Record<string, number | NodeJS.Timeout | undefined>;
  startCommandTimer: (type: { type: Command; participantId: string }, ttlMs?: number) => void;
  clearCommandTimer: (type: Command) => void;
  setOnExpireHandler: (fn: ((type: { type: Command; participantId: string }) => void) | null) => void;
  onExpireHandler: ((type: { type: Command; participantId: string }) => void) | null;

  showOriginalCaptions: boolean;
  setShowOriginalCaptions: (showOriginalCaptions: boolean) => void;

  isSidePanelScrollAtBottom: boolean;
  setIsSidePanelScrollAtBottom: (position: boolean) => void;
  isTranslatingSpeech: boolean;
  setIsTranslatingSpeech: (value: boolean) => void;
  captions: {
    participant: Participant;
    originalText: string;
    translatedText?: string;
    timestamp: number;
    isMe?: boolean;
  }[];
  setCaptions: (data: {
    participant: Participant;
    originalText: string;
    translatedText?: string;
    timestamp: number;
    isMe?: boolean;
  }) => void;
  originalSpeechByParticipant: Record<
    string,
    Record<
      string,
      {
        participant: Participant;
        text: string;
        lastUpdated: number;
      }
    >
  >;
  setOriginalSpeechByParticipant: (data: { participant: Participant; text: string; timestamp: number }) => void;
  consumeLastOriginalSpeechByParticipant: (participantId: string) => {
    consumed: { participant: Participant | null; text: string; lastUpdated: number } | null;
  };
  speechTimeouts: Record<string, NodeJS.Timeout | null>;
  setSpeechTimeout: ({ pid, timeoutId }: { pid: string; timeoutId: NodeJS.Timeout | null }) => void;
  lastWordTimestamps: Record<string, number>;
  setLastWordTimestamps: ({ pid, timestamp }: { pid: string; timestamp: number }) => void;
  userChangedVolume: boolean;
  setUserChangedVolume: (value: boolean) => void;
}

const mergeParticipants = (participants: Participant[]) => [
  ...new Map(participants.map((p) => [p.getId(), p])).values(),
];

const filterParticipant = (participants: Participant[], participant: Participant) =>
  participants.filter((p) => p.getId() !== participant.getId());

const checkTranslationNeeded = (participants: Participant[]): boolean => {
  const myParticipant = participants.find((p) => p.isMe());
  if (!myParticipant) return false;

  const myLanguage = myParticipant.getLanguage();

  // Check if other participants have a different language
  return participants.some((participant) => !participant.isMe() && participant.getLanguage() !== myLanguage);
};

export const useConferenceStore = create(
  subscribeWithSelector<ConferenceStore>((set, get) => ({
    conference: null,
    setConference: (conference: Conference | null) => set({ conference }),

    session: null,
    setSession: (session: Session | null) => set({ session }),

    participants: [],

    addParticipants: (participants: Participant[]) =>
      set((state) => {
        const newParticipants = mergeParticipants([...state.participants, ...participants]);
        const translationNeeded = checkTranslationNeeded(newParticipants);
        return {
          participants: newParticipants,
          translationEnabled: translationNeeded,
        };
      }),

    addParticipant: (participant: Participant) =>
      set((state) => {
        const newParticipants = mergeParticipants([...state.participants, participant]);
        const translationNeeded = checkTranslationNeeded(newParticipants);
        return {
          participants: newParticipants,
          translationEnabled: translationNeeded,
        };
      }),

    updateParticipant: (participant: Participant) =>
      set((state) => {
        const newParticipants = mergeParticipants([...filterParticipant(state.participants, participant), participant]);
        const translationNeeded = checkTranslationNeeded(newParticipants);
        return {
          participants: newParticipants,
          translationEnabled: translationNeeded,
        };
      }),

    removeParticipant: (participantId) =>
      set((state) => {
        const newParticipants = state.participants.filter((p) => p.getId() !== participantId);
        const translationNeeded = checkTranslationNeeded(newParticipants);

        const newAvScores = Object.fromEntries(Object.entries(state.avScores).filter(([id]) => id !== participantId));

        return {
          participants: newParticipants,
          translationEnabled: translationNeeded,
          avScores: newAvScores,
        };
      }),

    subscribeToParticipants: (
      callback: (oldParticipants: Participant[], newParticipants: Participant[]) => void
    ): (() => void) => {
      return useConferenceStore.subscribe(
        (state) => state.participants,
        (newParticipants: Participant[], oldParticipants: Participant[] = []) => {
          callback(oldParticipants, newParticipants);
        }
      );
    },

    addMockParticipant: () =>
      set((state) => {
        const newParticipants = [
          ...state.participants,
          new MockParticipant(
            Math.random().toString(16).slice(2),
            generateName(),
            _.sample(Object.values(Language)) as unknown as Language
          ),
        ];
        const translationNeeded = checkTranslationNeeded(newParticipants);
        return {
          participants: newParticipants,
          translationEnabled: translationNeeded,
        };
      }),
    removeMockParticipant: () =>
      set((state) => {
        const index = state.participants.findIndex((p) => p.isMock());
        if (index === -1) return state;

        const newParticipants = [...state.participants.slice(0, index), ...state.participants.slice(index + 1)];
        const translationNeeded = checkTranslationNeeded(newParticipants);
        return {
          participants: newParticipants,
          translationEnabled: translationNeeded,
        };
      }),
    me: null,
    setMe: (p) => set({ me: p }),

    loudestParticipantId: null,
    setLoudestParticipantId: (id: string | null) => set({ loudestParticipantId: id }),

    joinRequests: [],
    addJoinRequest: (request: ParticipantJoinRequest) =>
      set((state) => {
        const exists = state.joinRequests.some((r) => r.id === request.id);
        if (exists) return state;
        return { joinRequests: [...state.joinRequests, request] };
      }),
    removeJoinRequest: (requestId: string) =>
      set((state) => ({ joinRequests: state.joinRequests.filter((r) => r.id !== requestId) })),

    voiceAssistantState: null,
    setVoiceAssistantState: (state: VoiceAssistantState | null) => set({ voiceAssistantState: state }),

    voiceAssistantSpeaking: false,
    setVoiceAssistantSpeaking: (speaking: boolean) => set({ voiceAssistantSpeaking: speaking }),

    translationEnabled: false,
    setTranslationEnabled: (enabled: boolean) => set({ translationEnabled: enabled }),

    avScores: {},
    setParticipantAVScores: (id, score) =>
      set((state) => ({
        avScores: {
          ...state.avScores,
          [id]: score,
        },
      })),

    currentTranslatingState: {},
    setCurrentTranslatingState: (state) =>
      set(() => ({
        currentTranslatingState: state,
      })),
    myLanguage: null,
    setMyLanguage: (lang) => set(() => ({ myLanguage: lang })),

    participantVolumes: {},
    setParticipantVolumes: (ids) =>
      set(() => ({
        participantVolumes: ids,
      })),

    raisedCommands: [],
    setRaisedCommand: (command): void =>
      set((state) => ({
        raisedCommands: [...state.raisedCommands, command],
      })),
    deleteRaisedCommand: (command): void =>
      set((state) => ({
        raisedCommands: state.raisedCommands.filter((item) => item.type !== command),
      })),

    commandTimers: {},
    onExpireHandler: null,
    startCommandTimer: (command, ttlMs = 3000) => {
      const { type } = command;
      const prev = get().commandTimers[type];
      if (prev) {
        clearTimeout(prev);
      }
      const handler = get().onExpireHandler;

      const id = setTimeout(() => {
        get().clearCommandTimer(type);

        if (handler) {
          try {
            handler(command);
          } catch (e) {
            console.error('onExpireHandler error', e);
          }
        }
      }, ttlMs);

      set((s) => ({ commandTimers: { ...s.commandTimers, [type]: id } }));
    },
    clearCommandTimer: (type) => {
      const id = get().commandTimers[type];
      if (id) {
        clearTimeout(id);
        set((s) => {
          const timers = { ...s.commandTimers };
          delete timers[type];
          return { commandTimers: timers };
        });
      }
    },
    setOnExpireHandler: (fn) => set(() => ({ onExpireHandler: fn })),

    showOriginalCaptions: true,
    setShowOriginalCaptions: (showOriginalCaptions: boolean) => set(() => ({ showOriginalCaptions })),

    setIsSidePanelScrollAtBottom: (position) => set(() => ({ isSidePanelScrollAtBottom: position })),
    captions: [],
    isTranslatingSpeech: false,
    setCaptions: (data) => set((s) => ({ captions: [...s.captions, data] })),
    setIsTranslatingSpeech: (value) =>
      set(() => ({
        isTranslatingSpeech: value,
      })),

    isSidePanelScrollAtBottom: true,

    originalSpeechByParticipant: {},
    setOriginalSpeechByParticipant: ({ participant, text, timestamp }) =>
      set((state) => {
        const THRESHOLD = 3000;

        const originalSpeechByParticipant = state.originalSpeechByParticipant;
        const PID = participant.getId();

        const newEntry: typeof originalSpeechByParticipant = { ...originalSpeechByParticipant };

        if (!newEntry[PID]) {
          newEntry[PID] = {
            0: {
              participant,
              text: text.trim(),
              lastUpdated: timestamp,
            },
          };

          return { originalSpeechByParticipant: newEntry };
        }

        const ids = Object.keys(newEntry[PID]).map(Number);
        const lastId = ids[ids.length - 1];
        const lastRecord = newEntry[PID][lastId];

        const diff = timestamp - lastRecord.lastUpdated;

        if (diff > THRESHOLD) {
          const newId = lastId + 1;
          newEntry[PID][newId] = {
            participant,
            text: text.trim(),
            lastUpdated: timestamp,
          };
        } else {
          newEntry[PID][lastId] = {
            ...lastRecord,
            text: (lastRecord.text + text).trim(),
            lastUpdated: timestamp,
          };
        }

        return { originalSpeechByParticipant: newEntry };
      }),
    consumeLastOriginalSpeechByParticipant: (participantId) => {
      let consumed: {
        participant: Participant;
        text: string;
        lastUpdated: number;
      } | null = null;

      set((state) => {
        const entries = state.originalSpeechByParticipant[participantId];

        if (!entries) return state;

        const ids = Object.keys(entries).map(Number);
        const lastId = ids[ids.length - 1];

        consumed = entries[lastId];

        const updated = { ...state.originalSpeechByParticipant };
        delete updated[participantId][lastId];

        if (Object.keys(updated[participantId]).length === 0) {
          delete updated[participantId];
        }

        return {
          originalSpeechByParticipant: updated,
        };
      });

      return { consumed };
    },
    speechTimeouts: {},
    setSpeechTimeout: ({ pid, timeoutId }) =>
      set((store) => {
        return {
          speechTimeouts: { ...store.speechTimeouts, [pid]: timeoutId },
        };
      }),
    lastWordTimestamps: {},
    setLastWordTimestamps: ({ pid, timestamp }) =>
      set((store) => {
        return {
          lastWordTimestamps: { ...store.lastWordTimestamps, [pid]: timestamp },
        };
      }),

    userChangedVolume: false,
    setUserChangedVolume: (value) => set({ userChangedVolume: value }),
  }))
);
